/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig/hooks/useFeatureFlag';
import type { Labels } from '@/types/i18n';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import root from 'window-or-global';

/**
 * Get the localized labels. Pass a labelMap object to get a type-safe return type.
 *
 * Important: Always use a labelMap that is declared at the top level of a module (local or imported)
 *
 * Correct ✅
 * ```ts
 * import { externalLabelMap } from '../';
 * const labelMap = {};
 *
 * const MyComponent = () => {
 *    const labels = useLabels(labelMap);
 *    const externalLabels = useLabels(externalLabelMap);
 * }
 * ```
 *
 * Incorrect ❌
 * ```ts
 * const MyComponent = () => {
 *   const labelMap = {};
 *
 *   const labels = useLabels(labelMap);
 *   const otherLabels = useLabels({});
 * }
 * ```
 */
export const useLabels = <T extends Record<string, string>>(labelMap?: T): T => {
  const labelLoggingEnabled = useFeatureFlag('LABEL_LOGGING_ENABLED') === 'true';

  const select = useCallback((data: Labels) => {
    const handler: ProxyHandler<Labels> = {
      get(data, prop: string, receiver) {
        if (labelMap) {
          const labelKey = labelMap[prop];
          const labelValue = Reflect.get(data, labelKey, receiver);

          if (labelLoggingEnabled && !labelValue) {
            root.missingLabels?.add(labelKey);
          }

          return labelValue || labelKey;
        } else {
          const labelValue = Reflect.get(data, prop);

          if (labelLoggingEnabled && !labelValue) {
            root.missingLabels?.add(prop);
          }

          return labelValue || prop;
        }
      },
    };

    const proxy = new Proxy<Labels>(data, handler);

    return proxy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: labels } = useQuery({
    queryKey: labelsKey(),
    queryFn: () => {
      throw new Error('Query prefetch failed');
    },
    staleTime: Infinity,
    select,
  });

  return (labels ?? labelMap ?? {}) as T;
};

/**
 * @deprecated Use the `useLabels` hook instead
 */

export const useLabel = (label: string) => {
  const labelLoggingEnabled = useFeatureFlag('LABEL_LOGGING_ENABLED') === 'true';

  const query = useQuery({
    queryKey: labelsKey(),
    queryFn: () => {
      throw new Error('Query prefetch failed');
    },
    select: useCallback(
      (data: Labels) => {
        if (labelLoggingEnabled && !data[label]) {
          root.missingLabels?.add(label);
        }

        return data[label];
      },
      [label, labelLoggingEnabled],
    ),
    staleTime: Infinity,
  });

  return query.data ?? label;
};

export const labelsKey = () => ['labels'];
